
import React, { useState, useMemo } from 'react';
import { Solicitacao } from '@/types/solicitacao';
import { useSolicitacaoCIs, CIAtivo, CILicenca } from '@/hooks/useSolicitacaoCIs';
import { useAtivos } from '@/hooks/useAtivos';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Trash2, Plus, HardDrive, KeyRound, Search } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface Props {
  requisicao: Solicitacao;
}

// --- Seção genérica de CI ---
function CISection<T extends { id: string }>({
  title,
  icon: Icon,
  items,
  columns,
  renderRow,
  onRemove,
  onAdd,
  searchPlaceholder,
  searchItems,
  renderSearchItem,
  isPending,
}: {
  title: string;
  icon: React.ElementType;
  items: T[];
  columns: string[];
  renderRow: (item: T) => React.ReactNode;
  onRemove: (id: string) => void;
  onAdd: (id: string) => void;
  searchPlaceholder: string;
  searchItems: { id: string; label: string; sublabel?: string }[];
  renderSearchItem: (item: { id: string; label: string; sublabel?: string }) => React.ReactNode;
  isPending: boolean;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return searchItems;
    const term = searchTerm.toLowerCase();
    return searchItems.filter(s => s.label.toLowerCase().includes(term) || (s.sublabel && s.sublabel.toLowerCase().includes(term)));
  }, [searchItems, searchTerm]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <h4 className="font-medium text-sm">{title}</h4>
          <span className="text-xs text-muted-foreground">({items.length})</span>
        </div>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1">
              <Plus className="h-3 w-3" /> Adicionar
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-2" align="end">
            <div className="relative mb-2">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
            <ScrollArea className="max-h-48">
              {filtered.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-3">Nenhum item encontrado</p>
              ) : (
                filtered.map(item => (
                  <button
                    key={item.id}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-accent text-sm flex items-center justify-between"
                    disabled={isPending}
                    onClick={() => {
                      onAdd(item.id);
                      setOpen(false);
                      setSearchTerm('');
                    }}
                  >
                    {renderSearchItem(item)}
                  </button>
                ))
              )}
            </ScrollArea>
          </PopoverContent>
        </Popover>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">Nenhum item vinculado</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map(col => (
                <TableHead key={col}>{col}</TableHead>
              ))}
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(item => (
              <TableRow key={item.id}>
                {renderRow(item)}
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => onRemove(item.id)} disabled={isPending}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

export const RequisicaoCIsEnvolvidos = ({ requisicao }: Props) => {
  const { ativos, licencas, isLoading, addCI, removeCI } = useSolicitacaoCIs(requisicao.id);
  const { data: allAtivos } = useAtivos();

  // Buscar licenças disponíveis
  const { data: allLicencas } = useQuery({
    queryKey: ['cmdb-licencas-all'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cmdb_software_licencas')
        .select('id, chave_licenca, status, cmdb_softwares(nome)');
      return (data || []).map((l: any) => ({
        id: l.id,
        software_nome: l.cmdb_softwares?.nome || 'N/A',
        chave_licenca: l.chave_licenca,
        status: l.status,
      }));
    },
  });

  const existingAtivoIds = ativos.map(a => a.ci_id);
  const existingLicencaIds = licencas.map(l => l.ci_id);

  const ativosSearchItems = useMemo(() => {
    return (allAtivos || [])
      .filter(a => !existingAtivoIds.includes(a.id))
      .map(a => ({ id: a.id, label: a.nome, sublabel: a.tipo_ativo?.nome || undefined }));
  }, [allAtivos, existingAtivoIds]);

  const licencasSearchItems = useMemo(() => {
    return (allLicencas || [])
      .filter(l => !existingLicencaIds.includes(l.id))
      .map(l => ({ id: l.id, label: l.software_nome, sublabel: l.chave_licenca || undefined }));
  }, [allLicencas, existingLicencaIds]);

  if (isLoading) {
    return <p className="text-center py-8 text-muted-foreground">Carregando...</p>;
  }

  return (
    <div className="space-y-6 py-4">
      <CISection<CIAtivo>
        title="Dispositivos Envolvidos"
        icon={HardDrive}
        items={ativos}
        columns={['Nome', 'Tipo', 'Status']}
        renderRow={item => (
          <>
            <TableCell className="font-medium">{item.nome}</TableCell>
            <TableCell>{item.tipo_nome || '-'}</TableCell>
            <TableCell>{item.status_operacional || '-'}</TableCell>
          </>
        )}
        onRemove={id => removeCI.mutate(id)}
        onAdd={id => addCI.mutate({ ciTipo: 'ativo', ciId: id })}
        searchPlaceholder="Buscar ativo por nome..."
        searchItems={ativosSearchItems}
        renderSearchItem={item => (
          <span>
            {item.label}
            {item.sublabel && <span className="text-xs text-muted-foreground ml-1">({item.sublabel})</span>}
          </span>
        )}
        isPending={addCI.isPending || removeCI.isPending}
      />

      <CISection<CILicenca>
        title="Licenças Envolvidas"
        icon={KeyRound}
        items={licencas}
        columns={['Software', 'Chave', 'Status']}
        renderRow={item => (
          <>
            <TableCell className="font-medium">{item.software_nome}</TableCell>
            <TableCell>{item.chave_licenca || '-'}</TableCell>
            <TableCell>{item.status || '-'}</TableCell>
          </>
        )}
        onRemove={id => removeCI.mutate(id)}
        onAdd={id => addCI.mutate({ ciTipo: 'licenca', ciId: id })}
        searchPlaceholder="Buscar licença por software..."
        searchItems={licencasSearchItems}
        renderSearchItem={item => (
          <span>
            {item.label}
            {item.sublabel && <span className="text-xs text-muted-foreground ml-1">({item.sublabel})</span>}
          </span>
        )}
        isPending={addCI.isPending || removeCI.isPending}
      />
    </div>
  );
};
